import React from 'react';
import { Button } from 'antd';
import PropTypes from 'prop-types';

/**
 * CustomButton component that extends Ant Design Button with predefined styles and action types
 */
const CustomButton = ({
  type = 'primary',
  size = 'default',
  onClick,
  icon,
  disabled = false,
  loading = false,
  children,
  style = {},
  className = '',
  block = false,
  danger = false,
  ghost = false,
  shape,
  htmlType = 'button',
  actionType, // Action type for predefined actions
  data, // Data to pass to the action handler
  theme = 'default', // Theme for predefined styles
  buttonType = '', // Specialized button type (switch, view, edit, delete)
}) => {
  // Define theme styles
  const themeStyles = {
    default: {},
    action: {
      borderRadius: '4px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '0 15px',
      height: '32px',
      fontSize: '14px',
    },
    danger: {
      backgroundColor: '#ff4d4f',
      borderColor: '#ff4d4f',
      color: '#fff',
    },
    success: {
      backgroundColor: '#52c41a',
      borderColor: '#52c41a',
      color: '#fff',
    },
    warning: {
      backgroundColor: '#faad14',
      borderColor: '#faad14',
      color: '#fff',
    },
  };

  // Define button type styles
  const buttonTypeStyles = {
    switch: {
      backgroundColor: '#1890ff',
      borderColor: '#1890ff',
      color: '#fff',
      fontWeight: 500,
      minWidth: '140px',
    },
    view: {
      backgroundColor: '#fff',
      borderColor: '#d9d9d9',
      color: '#262626',
      minWidth: '100px',
    },
    edit: {
      backgroundColor: '#fff',
      borderColor: '#d9d9d9',
      color: '#262626',
      minWidth: '100px',
    },
    delete: {
      backgroundColor: '#fff',
      borderColor: '#ff4d4f',
      color: '#ff4d4f',
      minWidth: '100px',
    },
    deactivate: {
      backgroundColor: '#fff',
      borderColor: '#ff4d4f',
      color: '#ff4d4f',
      minWidth: '100px',
    },
  };

  // Combine styles
  const buttonStyle = {
    ...(themeStyles[theme] || {}),
    ...(buttonTypeStyles[buttonType] || {}),
    ...style,
  };

  // Add shadow to primary buttons if not already specified
  if (type === 'primary' && !buttonStyle.boxShadow && !buttonType) {
    buttonStyle.boxShadow = '0 2px 0 rgba(0, 0, 0, 0.045)';
  }

  // If an action type is provided, handle it appropriately
  const handleClick = () => {
    if (actionType && data) {
      // The parent component should handle different action types
      // This just passes the actionType and data to the onClick handler
      onClick && onClick(actionType, data);
    } else {
      // Standard onClick behavior
      onClick && onClick();
    }
  };

  return (
    <Button
      type={buttonType ? (buttonType === 'switch' ? 'primary' : 'default') : type}
      size={size}
      onClick={handleClick}
      icon={icon}
      disabled={disabled}
      loading={loading}
      style={buttonStyle}
      className={className}
      block={block}
      danger={buttonType === 'delete' || buttonType === 'deactivate' ? true : danger}
      ghost={ghost}
      shape={shape}
      htmlType={htmlType}
    >
      {children}
    </Button>
  );
};

CustomButton.propTypes = {
  type: PropTypes.oneOf(['primary', 'default', 'dashed', 'text', 'link']),
  size: PropTypes.oneOf(['small', 'default', 'large', 'medium']),
  onClick: PropTypes.func,
  icon: PropTypes.node,
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  children: PropTypes.node,
  style: PropTypes.object,
  className: PropTypes.string,
  block: PropTypes.bool,
  danger: PropTypes.bool,
  ghost: PropTypes.bool,
  shape: PropTypes.oneOf(['circle', 'round']),
  htmlType: PropTypes.oneOf(['button', 'submit', 'reset']),
  actionType: PropTypes.string, // Type of action to perform
  data: PropTypes.any, // Data to pass to the action handler
  theme: PropTypes.oneOf(['default', 'action', 'danger', 'success', 'warning']), // Theme style
  buttonType: PropTypes.oneOf(['', 'switch', 'view', 'edit', 'delete', 'deactivate']), // Specialized button type
};

export default CustomButton; 


// WEBPACK FOOTER //
// ./src/scenes/SuperAdmin/util/CustomButton.js